// Where the creatures stand on the stage (pure, no Pixi).
export interface StageLayoutCell {
  x: number;
  /** Feet y. */
  y: number;
}

export interface StageLayout {
  cells: StageLayoutCell[];
  /** Nominal creature body height (px). */
  size: number;
  cellW: number;
  fontSize: number;
}

/**
 * Lay out `n` creatures in up to 4 staggered rows so each is as big as possible. The front row
 * stands near the bottom (on the habitat's ground), back rows step up the hill behind it.
 */
export function layoutCreatures(n: number, w: number, h: number): StageLayout {
  if (n <= 0) return { cells: [], size: 0, cellW: 0, fontSize: 11 };
  const padX = Math.min(24, w * 0.03);
  const top = h * 0.1;
  let best = { rows: 1, size: 0, cellW: 0, rowH: 0 };
  for (let rows = 1; rows <= 4; rows++) {
    const cols = Math.ceil(n / rows);
    const cellW = (w - padX * 2) / (cols + (rows > 1 ? 0.5 : 0));
    const fontSize = Math.max(9, Math.min(12, cellW * 0.16));
    const usable = h - top - fontSize - 10;
    const rowH = usable / rows;
    // Creature (with jump headroom) must fit a row; the width must leave room between creatures.
    const size = Math.min(cellW * 0.5, rowH * 0.62, 78);
    if (size > best.size * 1.08) best = { rows, size, cellW, rowH };
  }
  const { rows, size, cellW, rowH } = best;
  const fontSize = Math.max(9, Math.min(12, cellW * 0.16));
  const cols = Math.ceil(n / rows);
  const frontY = h - fontSize - 10;
  const cells: StageLayoutCell[] = [];
  for (let i = 0; i < n; i++) {
    // Row 0 is the front row; fill it first so the most creatures stand on the ground.
    const row = Math.floor(i / cols);
    const col = i % cols;
    const inRow = Math.min(cols, n - row * cols);
    const stagger = rows > 1 && row % 2 === 1 ? cellW / 2 : 0;
    const rowW = inRow * cellW;
    const x = (w - rowW) / 2 + cellW / 2 + col * cellW + stagger - (rows > 1 ? cellW / 4 : 0);
    cells.push({ x, y: frontY - row * rowH });
  }
  return { cells, size, cellW, fontSize };
}
